import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { createTeam } from '../lib/teams';
import { useTeam } from '../contexts/TeamContext';

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateTeamModal({ isOpen, onClose }: CreateTeamModalProps) {
  const [teamName, setTeamName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { refreshTeams, switchTeam } = useTeam();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newTeam = await createTeam({ name: teamName.trim() });
      await refreshTeams();
      await switchTeam(newTeam.id);
      setTeamName('');
      onClose();
    } catch (err) {
      console.error('Failed to create team:', err);
      setError(err instanceof Error ? err.message : 'Failed to create team');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setTeamName('');
      setError(null);
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white rounded-sm shadow-2xl w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Create New Team</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Team Name *
              </label>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="Company Team"
                className="w-full px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                autoFocus
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-sm">
              Teams allow you to collaborate on projects with other users. You'll be the team owner and can invite members later.
            </div>
          </form>
        </div>
        
        <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-sm text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!teamName.trim() || isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Creating...
              </div>
            ) : (
              'Create Team'
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Get the modal root element
  const modalRoot = document.getElementById('modal-root');
  
  // If modal root doesn't exist, fallback to normal rendering
  if (!modalRoot) {
    console.warn('Modal root not found, rendering modal without portal');
    return modalContent;
  }
  
  // Render the modal using a portal with error boundary
  try {
    return createPortal(modalContent, modalRoot);
  } catch (error) {
    console.error('Portal creation failed:', error);
    return modalContent;
  }
}